/**
 * WORKOUT SESSION FORM - Quick entry modal for logging workouts
 */

import React, { useState } from 'react';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { apiClient } from '../api/apiConfig';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface WorkoutSessionFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const WorkoutSessionForm: React.FC<WorkoutSessionFormProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    workout_type: 'strength',
    duration_minutes: '',
    intensity: 'moderate',
    rpe: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        ...formData,
        duration_minutes: parseInt(formData.duration_minutes, 10),
        rpe: formData.rpe ? parseInt(formData.rpe, 10) : null
      };

      await apiClient.post('/api/workout-sessions/', payload);
      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      console.error('Error creating workout session:', err);
      setError(err.response?.data?.error || 'Failed to log workout');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      workout_type: 'strength',
      duration_minutes: '',
      intensity: 'moderate',
      rpe: '',
      notes: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: COLORS.bg.panel,
        border: `1px solid ${COLORS.border.default}`,
        borderRadius: RADIUS,
        padding: SPACING[32],
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: SPACING[24]
        }}>
          <h2 style={{
            fontSize: TYPOGRAPHY.size.xl,
            fontWeight: 600,
            color: COLORS.text.primary,
            margin: 0
          }}>
            Log Workout
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: COLORS.text.secondary,
              padding: SPACING[8]
            }}
          >
            <XMarkIcon style={{ width: 24, height: 24 }} />
          </button>
        </div>

        {error && (
          <div style={{
            padding: SPACING[16],
            background: 'rgba(239, 68, 68, 0.1)',
            border: `1px solid rgb(239, 68, 68)`,
            borderRadius: RADIUS,
            color: COLORS.status.error,
            marginBottom: SPACING[24],
            fontSize: TYPOGRAPHY.size.sm
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
          </div>

          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Workout Type
            </label>
            <select
              value={formData.workout_type}
              onChange={(e) => setFormData({ ...formData, workout_type: e.target.value })}
              required
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            >
              <option value="strength">Strength Training</option>
              <option value="running">Running</option>
              <option value="cycling">Cycling</option>
              <option value="swimming">Swimming</option>
              <option value="hiit">HIIT</option>
              <option value="zone2">Zone 2 Cardio</option>
              <option value="intervals">Intervals</option>
              <option value="sport">Sport/Recreation</option>
              <option value="mobility">Mobility/Stretching</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Duration (minutes)
            </label>
            <input
              type="number"
              value={formData.duration_minutes}
              onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
              required
              min="1"
              placeholder="60"
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
          </div>

          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Intensity
            </label>
            <select
              value={formData.intensity}
              onChange={(e) => setFormData({ ...formData, intensity: e.target.value })}
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            >
              <option value="easy">Easy</option>
              <option value="moderate">Moderate</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          <div style={{ marginBottom: SPACING[24] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              RPE (1-10, optional)
            </label>
            <input
              type="number"
              value={formData.rpe}
              onChange={(e) => setFormData({ ...formData, rpe: e.target.value })}
              min="1"
              max="10"
              placeholder="8"
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base
              }}
            />
          </div>

          <div style={{ marginBottom: SPACING[32] }}>
            <label style={{
              display: 'block',
              fontSize: TYPOGRAPHY.size.sm,
              fontWeight: 500,
              color: COLORS.text.secondary,
              marginBottom: SPACING[8]
            }}>
              Notes (optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              placeholder="Any additional notes about this workout..."
              style={{
                width: '100%',
                padding: SPACING[16],
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                fontSize: TYPOGRAPHY.size.base,
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{
            display: 'flex',
            gap: SPACING[16],
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: `${SPACING[12]} ${SPACING[24]}`,
                background: COLORS.bg.base,
                border: `1px solid ${COLORS.border.default}`,
                borderRadius: RADIUS,
                color: COLORS.text.secondary,
                cursor: 'pointer',
                fontSize: TYPOGRAPHY.size.base,
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: `${SPACING[12]} ${SPACING[24]}`,
                background: COLORS.accent.default,
                border: 'none',
                borderRadius: RADIUS,
                color: COLORS.text.primary,
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: TYPOGRAPHY.size.base,
                fontWeight: 500,
                opacity: submitting ? 0.6 : 1
              }}
            >
              {submitting ? 'Saving...' : 'Log Workout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkoutSessionForm;
